import React, { PropsWithChildren, useCallback, useMemo, useRef } from "react";

import SortBy from "./Components/SortBy/SortBy";
import {
  BackgroundColor,
  classForBackground,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import { FYCQuery } from "./FYCQueryBuilder";
import * as styles from "./FindYourCruise.module.scss";
import Stats from "./Components/Stats/Stats";
import { FirstLastDepartureCruiseDates } from "types/graphql-types";
import { ConfiguredInstantSearch } from "components/Algolia/ConfiguredInstantSearch";
import SearchResults from "./Components/SearchResults/SearchResults";
import { scrollToSection } from "src/utils/inPageNavigation";
import InPageFilters, {
  InPageFiltersProps
} from "./Components/InPageFilters/InPageFilters";
import { msg } from "@lingui/core/macro";
import { LinguiI18n } from "src/utils/localization/lingui-helpers";
import { useLingui } from "@lingui/react/macro";
import ConfiguredConfigure from "components/Algolia/ConfiguredConfigure";
import { useSearchAnalytics } from "components/Algolia/algoliaAnalytics";
import { PromoAlgoliaCodeMapping } from "components/Algolia/utils";
import { buildAlgoliaRouting } from "./routing";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { cruiseAttributesToRetrieve } from "./Components/utils";
import { useFYC } from "./useFYC";

type Props = {
  initialSearchQuery: FYCQuery;
  resultsPageSize: number;
  firstLastDepartureCruiseDates: FirstLastDepartureCruiseDates;
  backgroundColor: BackgroundColor;
  promoAlgoliaCodeMapping: PromoAlgoliaCodeMapping;
};

export type AlgoliaFYCProps = Props & Omit<InPageFiltersProps, "indexName">;

const AlgoliaFYC: React.FC<PropsWithChildren<AlgoliaFYCProps>> = ({
  children,
  initialSearchQuery,
  resultsPageSize,
  backgroundColor,
  promoAlgoliaCodeMapping,
  ...filtersProps
}) => {
  const { localizedSearchIndexName, filterDefaultSearch } =
    useFYC(initialSearchQuery);
  const bgClass = classForBackground(backgroundColor);
  const language = useLanguage();
  const { countryCode } = useGeolocation();
  const { i18n } = useLingui();
  const promosBadgeContent = filtersProps.promosContent.filter(
    (p) => !!p.showInCruiseCard
  );
  const resultsRef = useRef<HTMLDivElement>(null);

  const { onStateChange } = useSearchAnalytics(
    "cruise-filters-changed",
    promoAlgoliaCodeMapping
  );

  const resetScrollOnPageClick = useCallback(() => {
    if (resultsRef.current) scrollToSection(resultsRef.current);
  }, []);

  const attributesToRetrieve = useMemo(
    () => cruiseAttributesToRetrieve(language, countryCode),
    [language, countryCode]
  );

  if (!localizedSearchIndexName) return null;

  const routing = buildAlgoliaRouting(
    localizedSearchIndexName,
    language,
    countryCode,
    promoAlgoliaCodeMapping
  );

  return (
    <ConfiguredInstantSearch
      indexName={localizedSearchIndexName}
      routing={routing}
      onStateChange={onStateChange}
    >
      <ConfiguredConfigure
        filters={filterDefaultSearch}
        hitsPerPage={resultsPageSize}
        attributesToRetrieve={attributesToRetrieve}
        distinct
      />

      <SectionContainer backgroundColor="transparent">
        <div className={styles.filtersContainer}>
          <InPageFilters
            {...filtersProps}
            indexName={localizedSearchIndexName}
          />
        </div>
      </SectionContainer>
      <div className={bgClass} data-content-name="cruise_search_results">
        <SectionContainer backgroundColor="transparent">
          <div ref={resultsRef} className={styles.resultStatsContainer}>
            <div className={styles.resultStatsContainerSort}>
              <div className={styles.resultStatsContainerLeft}>
                <Stats />
              </div>
              <div>
                <SortBy
                  id="fyc-sort-by"
                  items={sortByItemsDynamicReRanking(
                    i18n,
                    localizedSearchIndexName
                  )}
                />
              </div>
            </div>
          </div>
          <div>
            <SearchResults
              onPaginationChanged={resetScrollOnPageClick}
              hitComponentProps={{
                promosBadgeContent: promosBadgeContent,
                shipsContent: filtersProps.shipsContent,
                showDestination: filtersProps.showDestinationFilter,
                backgroundColor:
                  backgroundColor == "white" ? "neutral01" : "white"
              }}
            />
          </div>
          {children}
        </SectionContainer>
      </div>
    </ConfiguredInstantSearch>
  );
};

const sortByItems = (i18n: LinguiI18n, defaultSearchIndexName: string) => {
  return [
    {
      value: defaultSearchIndexName,
      label: i18n._(msg`Departure Date`)
    },
    {
      value: `${defaultSearchIndexName}__duration-asc`,
      label: i18n._(msg`Duration (Min to Max)`)
    },
    {
      value: `${defaultSearchIndexName}__duration-desc`,
      label: i18n._(msg`Duration (Max to Min)`)
    }
  ];
};

const sortByItemsDynamicReRanking = (
  i18n: LinguiI18n,
  defaultSearchIndexName: string
) => {
  return [
    {
      value: defaultSearchIndexName,
      label: i18n._(msg`Recommended`)
    },
    {
      value: `${defaultSearchIndexName}__departure-asc`,
      label: i18n._(msg`Departure Date`)
    },
    {
      value: `${defaultSearchIndexName}__duration-asc`,
      label: i18n._(msg`Duration (Min to Max)`)
    },
    {
      value: `${defaultSearchIndexName}__duration-desc`,
      label: i18n._(msg`Duration (Max to Min)`)
    }
  ];
};

export default AlgoliaFYC;
